import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeetingRepository } from 'repository/meetingRepository';
import RouletteModeIcon, { RouletteMode } from './RouletteModeIcon';
import { playSlotTick, playRaceFanfare, unlockSfx } from 'common/utils/sfx';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { IcSlot, IcClose, IcRaiseHand, IcCheck, IcWarning, IcDice } from 'components/common/icons/EmojiIcons';
import styles from './RouletteSlotModal.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface RouletteParticipant {
    userNo: number;
    nickname: string;
    profileImgUrl?: string;
}

export interface RouletteResultBurden {
    userNo: number;
    nickname: string;
    amount: number;
}

export interface RouletteSlotModalProps {
    open: boolean;
    meetingId: string;
    storeName: string;
    totalAmount: number;
    participants: RouletteParticipant[];
    currentUserNo: number | null;
    maxPerPersonCap?: number;
    onClose: () => void;
    onComplete: (result: { mode: string; burdens: RouletteResultBurden[]; fallback: boolean; fallbackReason?: string }) => void;
}

type Stage = 'consent' | 'spinning' | 'result' | 'error';

/** 뽑기 결과 네 가지의 뼈대 — 글자는 화면에서 t() 로 뽑습니다.
 *  모듈 로드 시점에 i18n.t 를 부르면 initI18n() 전에 평가돼 키가 그대로 보입니다. */
const MODES: { key: RouletteMode; labelKey: string }[] = [
    { key: 'SOLO', labelKey: 'games.modeSolo' },
    { key: 'DUO', labelKey: 'games.modeDuo' },
    { key: 'EQUAL', labelKey: 'games.modeEqual' },
    { key: 'TREAT', labelKey: 'games.hostTreat' },
];

const RouletteSlotModal: React.FC<RouletteSlotModalProps> = ({
    open, meetingId, storeName, totalAmount, participants, currentUserNo, maxPerPersonCap,
    onClose, onComplete,
}) => {
    const { t } = useTranslation();
    const [stage, setStage] = useState<Stage>('consent');
    const [agreedIds, setAgreedIds] = useState<Set<number>>(new Set());
    const [reelIdx, setReelIdx] = useState<number[]>([0, 0, 0]);
    const [result, setResult] = useState<{ mode: string; burdens: RouletteResultBurden[]; fallback: boolean; fallbackReason?: string } | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // 내 동의 — 자동 체크
    useEffect(() => {
        if (open && currentUserNo) setAgreedIds(new Set([currentUserNo]));
    }, [open, currentUserNo]);

    useBackDismiss(open, onClose);
    if (!open) return null;

    const allAgreed = participants.length > 0 && participants.every(p => agreedIds.has(p.userNo));

    const toggleAgree = (userNo: number) => {
        setAgreedIds(prev => {
            const next = new Set(prev);
            if (next.has(userNo)) next.delete(userNo);
            else next.add(userNo);
            return next;
        });
    };

    const spin = async () => {
        unlockSfx();
        setStage('spinning');
        // 릴 애니메이션 + 딸깍 사운드 (3초)
        const reelTimer = setInterval(() => {
            setReelIdx([
                Math.floor(Math.random() * MODES.length),
                Math.floor(Math.random() * MODES.length),
                Math.floor(Math.random() * MODES.length),
            ]);
            playSlotTick();
        }, 80);
        try {
            const res: any = await MeetingRepository.onPostRouletteSpin({
                meetingId,
                storeName,
                totalAmount,
                participantUserNos: participants.map(p => p.userNo),
                maxPerPersonCap,
            });
            const data = res?.data?.data;
            // 최소 2.5초는 돌리기 (연출)
            await new Promise(r => setTimeout(r, 2500));
            clearInterval(reelTimer);
            if (!data?.mode) {
                setErrorMsg(t('games.rouletteFail'));
                setStage('error');
                return;
            }
            // 최종 모드로 릴 정렬
            const finalIdx = MODES.findIndex(m => m.key === data.mode);
            setReelIdx([finalIdx, finalIdx, finalIdx]);
            setResult({
                mode: data.mode,
                burdens: (data.burdens || []).map((b: any) => ({
                    userNo: Number(b.userNo),
                    nickname: b.nickname,
                    amount: Number(b.amount),
                })),
                fallback: Boolean(data.fallbackApplied),
                fallbackReason: data.fallbackReason,
            });
            setTimeout(() => { setStage('result'); playRaceFanfare(); }, 400);
        } catch (e: any) {
            clearInterval(reelTimer);
            console.error('[Roulette] 스핀 실패', e);
            setErrorMsg(e?.response?.data?.message || t('games.rouletteRunFail'));
            setStage('error');
        }
    };

    const handleConfirm = () => {
        if (result) onComplete(result);
        onClose();
    };

    return (
        <div className={cls("roulette-overlay")} onClick={stage === 'result' || stage === 'error' ? onClose : undefined}>
            <div className={cls("roulette-modal")} onClick={e => e.stopPropagation()}>
                <header className={cls("roulette-header")}>
                    <span><IcSlot size={18} /> {t('games.rouletteTitle')}</span>
                    {stage !== 'spinning' && (
                        <button className={cls("roulette-close")} onClick={onClose}><IcClose size={18} /></button>
                    )}
                </header>

                <div className={cls("roulette-summary")}>
                    <div className={cls("rm-store")}>{storeName}</div>
                    <div className={cls("rm-total")}>
                        {t('meetingFinished.amountWon', { amount: totalAmount.toLocaleString('ko-KR') })}
                    </div>
                    <div className={cls("rm-participants")}>{t('games.targetCount', { count: participants.length })}</div>
                </div>

                {/* 슬롯 릴 */}
                <div className={cls("roulette-reels")}>
                    {reelIdx.map((i, idx) => (
                        <div key={idx} className={cls(`rm-reel ${stage === 'spinning' ? 'spinning' : ''}`)}>
                            <RouletteModeIcon mode={MODES[i].key} size={48} spinning={stage === 'spinning'} />
                        </div>
                    ))}
                </div>

                {stage === 'consent' && (
                    <>
                        <div className={cls("rm-consent-title")}><IcRaiseHand size={16} /> {t('games.allAgree')}</div>
                        <ul className={cls("rm-consent-list")}>
                            {participants.map(p => (
                                <li key={p.userNo} className={cls(agreedIds.has(p.userNo) ? 'agreed' : '')}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={agreedIds.has(p.userNo)}
                                            onChange={() => toggleAgree(p.userNo)}
                                        />
                                        <span>{p.nickname}{p.userNo === currentUserNo && ` (${t('games.meMark')})`}</span>
                                        {agreedIds.has(p.userNo) && <span className={cls("rm-check")}><IcCheck size={14} /></span>}
                                    </label>
                                </li>
                            ))}
                        </ul>
                        <div className={cls("rm-warn")}><IcWarning size={14} /> {t('games.rouletteWarn')}</div>
                        <div className={cls("rm-actions")}>
                            <button className={cls("rm-btn-cancel")} onClick={onClose}>{t('games.back')}</button>
                            <button className={cls("rm-btn-spin")} onClick={spin} disabled={!allAgreed}>
                                {allAgreed ? <><IcSlot size={16} /> {t('games.spin')}</> : t('games.agreedCount', { a: agreedIds.size, total: participants.length })}
                            </button>
                        </div>
                    </>
                )}

                {stage === 'spinning' && (
                    <div className={cls("rm-spinning")}>
                        <div className={cls("rm-spin-text")}><IcDice size={16} /> {t('games.spinning')}</div>
                    </div>
                )}

                {stage === 'result' && result && (
                    <>
                        <div className={cls("rm-result-badge")}>
                            <RouletteModeIcon mode={result.mode as RouletteMode} size={36} />
                            <span className={cls("rm-result-label")}>
                                {(() => {
                                    const mode = MODES.find(m => m.key === result.mode);
                                    return mode ? t(mode.labelKey) : result.mode;
                                })()}
                            </span>
                        </div>
                        {result.fallback && (
                            <div className={cls("rm-fallback")}><IcWarning size={14} /> {result.fallbackReason}</div>
                        )}
                        <ul className={cls("rm-result-list")}>
                            {result.burdens.map(b => (
                                <li key={b.userNo} className={cls(b.userNo === currentUserNo ? 'me' : '')}>
                                    <span className={cls("rm-burden-name")}>{b.nickname}{b.userNo === currentUserNo && ` (${t('games.meMark')})`}</span>
                                    <span className={cls("rm-burden-amount")}>
                                        {t('meetingFinished.amountWon', { amount: b.amount.toLocaleString('ko-KR') })}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <button className={cls("rm-btn-confirm")} onClick={handleConfirm}>{t('games.confirmReflect')}</button>
                    </>
                )}

                {stage === 'error' && (
                    <>
                        <div className={cls("rm-error")}>{errorMsg}</div>
                        <button className={cls("rm-btn-cancel")} onClick={onClose}>{t('games.close')}</button>
                    </>
                )}
            </div>
        </div>
    );
};

export default RouletteSlotModal;
